/**
 * What a link inside a Copilot (beta) answer should do. ACOP-152 test finding, AC-12.
 *
 * Deliberately free of React so the rule can be unit-tested directly — the same reasoning as
 * utils/nav.ts and utils/sourceLabels.ts.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 *
 * The answering model is told to cite with `[1]`, `[2]` (AVS OS rag-search-service.ts) and those
 * become the numbered pills. But when an answer ends "refer to these articles", the model sometimes
 * writes the list as markdown links whose target is only the source number:
 * `[Working with Outbound Shipments](#4)`. Captured on copilot-stgn 2026-10-27 for "How do I ship a
 * sales order?": six such links, each link's text exactly source N's title.
 *
 * Rendered as a plain `<a href="#4">` the browser resolves that against the SPA page —
 * `https://copilot-stgn.avsight.net/copilot-2#4` — and the click handler, which sends every link to
 * the embed page, opened an embed of `#4`. Neither does anything useful.
 *
 * The number is unambiguous: it is the same 1-based index the pills use, into the same `sources`
 * list. So the link is treated as a citation of that source and opens it in the same panel.
 *
 * ── Why the UI copes rather than AVS OS being asked to stop ────────────────────────────────────
 *
 * A model instruction is not a guarantee, and AVS OS is another repo. Whether to also change the
 * prompt is recorded for AVS OS triage (PRD Out-of-scope). This handles what the model actually writes.
 */

export type AnswerSource = {
  id?: string;
  title?: string;
  citationUrl?: string | null;
};

export type AnswerLinkTarget =
  | { kind: "citation"; source: AnswerSource }
  | { kind: "text" }
  | { kind: "link"; href: string | undefined };

/** `#` followed by digits and nothing else — the model's "link to source N". */
const SOURCE_NUMBER = /^#(\d+)$/;

/**
 * - `#N` with source N having something to open → `citation` (open it in the panel).
 * - `#N` with no such source, a source with nothing to open (the numbered pill is greyed out for
 *   those too), or any other fragment-only target (`#`, `#intro`) → `text`: it has nowhere to go
 *   inside this app, so it must not look like a link.
 * - everything else, including a missing target → `link`, exactly as before.
 */
export function resolveAnswerLink(
  href: string | undefined,
  sources: AnswerSource[] | undefined,
): AnswerLinkTarget {
  if (typeof href !== "string" || !href.startsWith("#")) return { kind: "link", href };
  const match = SOURCE_NUMBER.exec(href);
  const source = match ? sources?.[parseInt(match[1], 10) - 1] : undefined;
  return source?.citationUrl ? { kind: "citation", source } : { kind: "text" };
}
